// 文案转换层：后端下发状态 + 参数，这里集中全部展示文案（原型 eligibility-check.html 逐句对照）。
// tag 的 label/tip 仍在 components/EligibilityStatusTag 的共享词汇表里，这里只管模块体。
import { EBenefitPeriod, ECoverageLevel, ENetworkStatus, TBenefitScope } from 'types/insuranceEligibility';
import { TBarScene } from './derive';

export type TBarTone = 'ok' | 'problem' | 'bad';

// 结果 bar：tone + 一句话摘要
export const BAR_COPY: Record<TBarScene, { tone: TBarTone; text: string }> = {
    active: { tone: 'ok', text: 'Coverage is active' },
    partial: { tone: 'problem', text: 'Coverage for this visit was not confirmed' },
    carve: { tone: 'problem', text: 'Medical plan amounts only' },
    nonCovered: { tone: 'problem', text: 'This plan does not cover this service' },
    conflict: { tone: 'problem', text: 'The payer sent more than one answer' },
    unknown: { tone: 'problem', text: 'Coverage could not be determined' },
    oon: { tone: 'bad', text: 'Out of network for this patient' },
    inactive: { tone: 'bad', text: 'Coverage was not active on the date of service' },
    rejected: { tone: 'bad', text: 'The payer could not verify this patient' },
};

// 10/05 plan type 不在 template 的 OON：bar 只说「哪种 plan」，原因留给首行（原型 viewResult）
export const PLAN_TYPE_OON_BAR = (planType: string) => `Out of network: ${planType}`;

// 免责声明（原型 elig-disclaimer）：开头那半句加粗——整段三句都在限定一个「正面结论」，
// 加粗的正是要限定的那一句。拆成三段供 JSX 拼接
export const DISCLAIMER_LEAD = 'Active coverage is ';
export const DISCLAIMER_BOLD = 'not a guarantee of payment';
export const DISCLAIMER_TAIL =
    ', and this response does not confirm network participation for this patient. Fields marked "Not reported" ' +
    'were not returned by the payer. They do not indicate $0 and do not indicate a denial.';

// carve-out 触发的 No cost estimate：tag 的 hover 换这套（原型 tagTip 逐字——「payer 没回 copay」
// 对 carve-out 是假话：它回了，只是回的是这次问诊不在的那个 plan）。partial 同时成立时不换
export const CARVE_TAG_TIP =
    'Coverage is active and this visit can be billed. Psych visits for this member are administered by a ' +
    'different company, so the amounts on file are the medical plan and do not apply to a behavioral ' +
    'health visit.';

export const TILE_COPY = {
    notReported: 'Not reported',
    notReportedSub: 'by payer',
    conflictSub: 'payer sent both',
    calculated: '(calculated)',
    leftOf: (total: string) => `left of ${total}`,
    // 价格类 tile 的默认副行（payer 附言 note 优先）：copay 恒 per visit，coinsurance 恒 after deductible
    perVisit: 'per visit',
    afterDeductible: 'after deductible',
    // 累计类 tile 的副行（不翻转形态时）：剩余额优先，其次已用额
    remainingSub: (v: string) => `${v} remaining`,
    metSub: (v: string) => `${v} met`,
};

// tiles 下方的数字来源警示（costSharingBasis 驱动）：GENERAL/MIXED 才显示——好情况不确认
// （每张卡都确认会训练人跳过这行，坏情况出现时恰好没人读）；carve-out 块在场时抑制
// （它把同一件事说得更重：那些金额根本不适用）。
// copayAmount 不保证来自 behavioral health 行（2026-09-28 后端确认：basis=GENERAL 时它来自
// 全计划通用行 STC=30），所以这两句要连 Copay 一起免责，不能只讲 coinsurance。
// 句子里点名 Copay (Mental Health) / Coinsurance 两格：Copay (Medical) 恒为一般医疗门诊行
// （STC=98），自带标题、不在免责范围内，笼统说「上面的金额」会把它也扫进来。两格同名后必须带括号
// 后缀点名，否则「The Copay」指向不唯一
export const BASIS_COPY = {
    mixed:
        'The Copay (Mental Health) and Coinsurance above come from different service types, one behavioral ' +
        'health and one general medical, so they may not both apply to this visit.',
    general: (payer: string) =>
        `${payer} did not return cost sharing specific to behavioral health. The Copay (Mental Health) and ` +
        "Coinsurance above reflect the plan's general medical benefits and may not apply to this visit.",
};

export const TILE_LABELS = {
    copay: 'Copay (Mental Health)',
    copayMedical: 'Copay (Medical)',
    coinsurance: 'Coinsurance',
    deductible: 'Deductible',
    oopMax: 'Out-of-pocket max',
};

const PERIOD_LABEL: Record<EBenefitPeriod, string> = {
    [EBenefitPeriod.CALENDAR_YEAR]: 'Calendar year',
    [EBenefitPeriod.SERVICE_YEAR]: 'Service year',
    [EBenefitPeriod.VISIT]: 'Per visit',
    // scope 行理论上收不到（仅 visit limit 会给 MONTH），补全枚举防御
    [EBenefitPeriod.MONTH]: 'Per month',
};

const LEVEL_LABEL: Record<ECoverageLevel, string> = {
    [ECoverageLevel.INDIVIDUAL]: 'Individual',
    [ECoverageLevel.FAMILY]: 'Family',
};

// scope 行 "Calendar year · Individual · In network"：payer 没说的轴不出现
export const scopeLine = (scope?: TBenefitScope): string => {
    if (!scope) return '';
    const parts: string[] = [];
    if (scope.period) parts.push(PERIOD_LABEL[scope.period]);
    if (scope.coverageLevel) parts.push(LEVEL_LABEL[scope.coverageLevel]);
    if (scope.networkTier)
        parts.push(scope.networkTier === ENetworkStatus.IN_NETWORK ? 'In network' : 'Out of network');
    return parts.join(' · ');
};

// 服务类别 token → 可读词组（PSYCHOTHERAPY → Psychotherapy，MENTAL_HEALTH → Mental health；
// 文档口径只有这两个值，但按通用转换写——后端将来给新 token 也能体面显示）
const serviceTypeLabel = (token?: string): string => {
    const words = (token || '').trim().split('_').filter(Boolean).join(' ').toLowerCase();
    if (!words) return 'Psychotherapy';
    return words.charAt(0).toUpperCase() + words.slice(1);
};

// BH 块（原型逐字，payer/管理方点名；加粗段在 BhBlock 里以 JSX 拼接）
export const BH_COPY = {
    coveredTitle: (serviceType?: string) => `${serviceTypeLabel(serviceType)} is covered by this plan`,
    coveredBody: (payer: string) =>
        `${payer} administers behavioral health directly, not through a separate administrator.`,
    // 裸数字会被读成「还剩 N 次」——271 给的是计划上限，没说用了几次，必须明说
    visitLimitLead: 'The plan allows up to ',
    visitLimitBold: (quantity: number) => `${quantity} visits`,
    // 周期插语（visitLimitTimeQualifier）：VISIT 只表达单位、同缺省走裸句（原型口径）
    visitLimitPer: {
        [EBenefitPeriod.CALENDAR_YEAR]: ' per calendar year',
        [EBenefitPeriod.SERVICE_YEAR]: ' per service year',
        [EBenefitPeriod.MONTH]: ' per month',
    } as Partial<Record<EBenefitPeriod, string>>,
    visitLimitTail: ', and the payer did not say how many are left.',
    carveTitle: 'Psych visits are handled by another company',
    // 有管理方名字：两个名字对照（"Optum …, not Cigna"）；没有：payer 没回 BH benefits 的推断句
    carveWho: (payer: string) => ` administers behavioral health for this member, not ${payer}.`,
    carveNoAdmin: (payer: string) =>
        `${payer} returned no behavioral health benefits, which typically indicates a separate behavioral health administrator.`,
    // 共同结尾（"not" 加粗在 JSX）："The amounts above reflect the medical plan and do not apply …"
    carveTailLead: ' The amounts above reflect the medical plan and do ',
    carveTailBold: 'not',
    carveTailEnd: ' apply to a behavioral health visit. Verify coverage with that administrator separately.',
};

export const INFO_COPY = {
    // OON 行（原型逐字）：点名 payer、说「你的练所」而不是「这位 provider / 这位 member」，
    // 全红、关键短语加粗。payer 的 271 原话（ALL CAPS）刻意不印——同一个事实用更差的英文再说一遍，
    // 而 networkMessage 的措辞是给系统看的，不是给 provider 看的（原型 09/15 明确删掉了这段引用）
    oonLead: (payer: string) => `${payer} reports your practice as `,
    oonBold: 'out of network',
    oonTail: ' for this patient.',
    // 10/05：OON 不是 payer 说的，是平台规则判的——payer 答 active，但 271 的 plan type 不在 provider 的
    // service template 上。首句必须点名原因，否则会被读成「payer 说不在网」。两句（原型逐字）：
    // 红色首句说结论与后果，灰色次句说为什么病人能约上、以及两条出路
    planTypeLead: 'Out of network because ',
    planTypeTail: (whose: string) =>
        ` isn’t on ${whose} service template. The plan is active, but a claim for this visit can’t be billed to it.`,
    // 次句的「谁接这个 plan」随 whose 走：owner 代看成员时也是 this provider（Stacey 10-21）
    planTypeNext: (payer: string, planType: string, isOther?: boolean) =>
        `The patient booked because ${payer} is listed on the profile. Tell them before the visit, or add ` +
        `${planType} to the template if ${isOther ? 'this provider does' : 'you do'} take it.`,
    // 「谁的」template：my/solo 是自己的；practice 视图归被查的那位 provider（与 TEMPLATE_GAP_COPY 同口径，不点名）
    planTypeWhoseSelf: 'your',
    planTypeWhoseOther: 'this provider’s',
    addToTemplateBtn: 'Add it to the template',
    // 未定价两句（原型逐字）：先说可能是我们的请求不对，再说 payer 不答之前不能报价
    partial1:
        'The payer answered for the plan but sent nothing for this visit, so we cannot say it is covered or what ' +
        'it costs. Check that the visit type and date on the record are the ones you are billing, then re-check.',
    partial2: 'If it comes back empty again, the insurer has to answer it before you quote the patient a price.',
    conflictIntro: 'The payer returned more than one value for the same cost. Neither is chosen for you:',
    // 每行的 payer 原文标签（additionalInformationDescription）；没附标签的行也明说（原型 .conf-note.none）
    conflictRowLabel: (label: string) => `the payer labelled this row “${label}”`,
    conflictRowNoLabel: 'the payer sent no label on this row',
    conflictShared: 'The rows are identical on every other axis the payer reported.',
    // 有任一行带标签时追加（原型逐字）：标签是 payer 原话，可辅助判断但不能当最终报价
    conflictLabelNote:
        'Those labels are the payer’s own wording. If you know which one this visit bills as, that is the ' +
        'likely amount, but it is not confirmed and should not be quoted to the patient as final.',
    // service-not-covered 三句（原型逐字）：排除≠断保 → payer 附言 → 后果句；"not cover" 加粗在 JSX
    sncLead: (payer: string) => `${payer} says this plan does `,
    sncNotCover: 'not cover',
    sncTail: ' this service. The plan itself is active, so this is an exclusion rather than a lapse in coverage.',
    sncNote: (note: string) => `Payer note: ${note}`,
    sncSelfPay:
        'A claim for this visit will be denied, so unless the patient has other coverage it is self-pay. ' +
        'Any amounts above belong to services this plan does cover and do not describe this visit.',
    messagePatientBtn: 'Message patient',
    // prior auth 行已整条不渲染（2026-09-17 拍板：按原型渲染结果对齐，那一行屏幕上从未出现过）。
    // 适配层仍解析 priorAuthRequired，改主意时把 InfoLines 里的那段和这三句文案一起加回来
    // inactive 两句（原型逐字）：bar 已说「没生效」，这里只说后果；结尾带自动 re-check 承诺。
    // 有失效日期时句首拼 "Coverage on this plan ended {date}. "（加粗日期在 JSX）
    inactiveEndedLead: 'Coverage on this plan ended ',
    inactive1: 'A claim sent to it will be denied, so unless the patient has other coverage this visit is self-pay.',
    inactive2:
        'Most often the patient has changed plans and the record has not caught up. Requesting the card asks for ' +
        'the current one, and the check re-runs by itself when it arrives.',
    rejectedExp:
        'This does not indicate a lack of coverage. The payer could not match the details submitted, which is usually a data entry error.',
    unknown: 'The payer answered, but the coverage status could not be determined from the response.',
};

// 计费口径（后端拍板）：只有 SYSTEM_ERROR / PAYER_DOWN 不收费，其余（含 AAA 拒识、缓存命中）都收。
// chip 只在 cost modal 被关掉后出现（否则是第三遍重复）；PAYER_DOWN 用原型 no-charge 句替代 chip
export const FOOTER_COPY = {
    charged: '+$0.25 to this month',
    payerDownNoCharge: 'No coverage was confirmed, so there is no charge.',
};

export const IDLE_COPY = {
    managedWithBooking:
        'Klarity verifies eligibility about 2 seconds after the patient completes booking, so their copay shows in ' +
        'the app while they are still in intake. It runs again before the visit, on any insurance change, and ' +
        'monthly. No cost and no action needed.',
    managedNoBooking: 'Checks run automatically when a patient books through Klarity.',
    manualHint: '$0.25 per check on your current plan, billed to your monthly invoice.',
    checkCta: 'Check eligibility',
    // manual 唯一的转化入口（PRD §8 成功指标：看过 auto-check 的人的 program 转化）；
    // 落到 Insurance billing 页，未注册者看到的就是 pitch——那是目的地不是兜底
    enrollLink: 'Enroll in Managed Billing to run eligibility checks at no cost →',
};

// ---------- 多份 primary（10/05）----------
// 从没查过时先单选一张再 check（参考原型 "Choose the plans to check" 卡；那张是多选 + 琥珀，这里单选 + 蓝：
// 没有出错，只是缺一个选择——与「先选一位 provider」同一个蓝）
export const PICK_INSURANCE_COPY = {
    barLine: 'Choose the insurance to check',
    defaultTag: 'Default',
    ids: (memberId?: string, groupId?: string) =>
        [memberId ? `Member ID ${memberId}` : '', groupId ? `Group ${groupId}` : ''].filter(Boolean).join(' · '),
    cta: (price?: string) => (price ? `Check this plan · ${price}` : 'Check this plan'),
    notDefault: 'This isn’t the patient’s default insurance. Checking it doesn’t change that.',
};

// 10/21：选择卡 / 「查另一张」的分组标签（小号大写），让 provider 一眼知道每张是主是次
export const INSURANCE_GROUP_COPY = {
    primary: 'Primary',
    secondary: 'Secondary',
};

// 结果卡 Last checked 下一行：这张卡答的是哪张（一律显示，多张主保险时也要分清；原型 metaRow）
export const PLAN_SCOPE_LINE = (carrier: string, secondary: boolean) =>
    `${carrier} · ${secondary ? 'Secondary' : 'Primary'} insurance`;

// 失败后「查另一张」（仅 not enrolled，原型 otherOffer 逐字）：自己档案上的另一张比 $1.50 的目录搜索更该先试
export const OTHER_PRIMARY_COPY = {
    leadOne: 'The patient has another insurance on file.',
    leadMany: (n: number) => `The patient has ${n} other insurances on file.`,
    tailOne: ' You can check it instead.',
    tailMany: ' You can check one of them instead.',
    tailNone: ' None of them worked for this visit.',
    title: (carrier?: string, plan?: string) => [carrier, plan].filter(Boolean).join(' · '),
    tried: (label: string, date: string) => `${label} · checked ${date}`,
    selectCta: 'Select a plan',
    checkCta: (price: string) => `Check this plan · ${price}`,
    // cost modal 的 other 模式正文（原型 openCostModal('other')）
    // 查的是次保险时点名「secondary」（原型 10/21）
    costBody: (carrier: string, secondary?: boolean) =>
        `Klarity will request coverage and benefits from ${carrier}, the patient’s ${
            secondary ? 'secondary' : 'other'
        } insurance on file.`,
};

// 结果卡落在非 default 那张上、且它可用：一键设为 default（原型 defaultRow）
export const SET_DEFAULT_COPY = {
    line: 'This isn’t the patient’s default insurance. Make it the default so visits are billed to it.',
    cta: 'Set as default',
    done: (name: string) => `${name} is now the patient’s default insurance.`,
};

// 「查另一张」之后的 toast（原型 runOtherCheck）
export const OTHER_RESULT_TOAST = {
    active: (name: string) => `${name} is active. It’s on the card now.`,
    other: (name: string, label: string) => `${name} came back ${label}.`,
};

export const RECHECK_COPY = {
    label: 'Re-check',
    manualTip: "Runs a new check with the insurance company. Adds $0.25 to this month's invoice.",
    // 中间半句不可省：「limit reached」单独出现会被读成「这个患者今天没法验证」——错误且昂贵的误读
    managedLimitTip:
        'Daily re-check limit reached for this patient. Automatic checks are unaffected, and coverage is still ' +
        'verified before the visit. Resets tomorrow.',
};

export const COST_MODAL_COPY = {
    title: 'Run this eligibility check?',
    body: (carrier: string) => `Klarity will request coverage and benefits from ${carrier}.`,
    thisCheck: 'This check',
    // practice 视图下 check 以归属 provider 身份（override）执行：费用与本月累计都落在该 provider，"Your" 错位
    runningTotal: 'Your total this month',
    runningTotalPractice: "This provider's total this month",
    foot: 'You are not charged now. This goes on your monthly invoice.',
    footPractice: "You are not charged now. This goes on the provider's monthly invoice.",
    dontAsk: "Don't ask again",
    cancel: 'Cancel',
    run: 'Run check',
};

export const LOADING_COPY = 'Verifying coverage with the payer via Stedi…';
export const ADV_LOADING_COPY = 'Searching the payer directory…';

// ---------- 二次失败（仅 managed；政策 SOP scope 就是 billing program 内的 provider） ----------
// 第一次失败不显示任何东西：Klarity 的自动轮（更宽搜索 → 患者自纠 → 复查）在跑，汇报未完成的步骤是错的。
// ⚠️ 「one hour before the visit」与政策文档 v1.4 不一致（标准 deadline 是 T-visit / -24h / -48h，
// 唯一的 -2h+1h 窗口只在 §9.2 当日预约）；原型即如此标注，待 Billing Ops v1.5 定稿后统一改这里
export const OUTREACH_COPY = {
    lead: 'We have checked twice and still cannot verify this plan.',
    ask: 'A message from you is the last thing that can change that.',
    endingCash:
        'If it does not, we offer the patient your cash price. If they decline, we cancel one hour before the visit and refund your lead-gen fee.',
    endingNoCash:
        'There is no cash price on this listing to fall back on, so if nothing changes we cancel one hour before the visit, refund your lead-gen fee, and help the patient find another suitable provider.',
};

// 修复卡 Recommended fix 的二次失败替换：让患者改资料这步系统已经做过、复查也失败了，
// 再指字段就是指示提供者重复一个已失败的步骤；文案说「答案没变」而不是「患者没回」——
// 二次失败最常见的路径恰恰是患者回了、改了、复查仍失败
export const SECOND_FAIL_FIX = {
    cash: 'Two checks have come back the same. Ask the patient whether to keep the visit at your self-pay price.',
    noCash: 'Two checks have come back the same. Ask the patient for a different card before the visit is cancelled.',
};

// ---------- no coverage detail（10/22，Stacey 选 A，文案原文）----------
// 271 回了 200 但没有任何 benefit 信息：payer 答了，只是没说 plan 是否有效。不是系统错误、不是拒识，
// 也不该叫人改档案——唯一出路是打电话问 insurer。不计费：脚注只给 manual（managed 全程无计费文案）
export const NO_COVERAGE_DETAIL_COPY = {
    barLine: 'The insurer didn’t return coverage details',
    // 原型分两行：先说事实（不是拒保），再说下一步
    body:
        'The insurer answered, but sent no coverage information for this patient, so we can’t tell whether the ' +
        'plan is active. This is not a denial.',
    next: 'Call the insurer to confirm coverage before the visit.',
    noCharge: 'No charge for this check.',
};

// ---------- system error（transport 失败，无 271 可读；原型 viewSystemError） ----------
// 与 AAA 拒识分开：42/80（PAYER_DOWN）是 payer 给了「答不了」的答复，走修复卡；
// SYSTEM_ERROR 是请求根本没到达，事件而非状态——有历史结果时只 toast、结果卡保持原样，
// 只有从未有过完成的 check 时才整卡渲染（红 bad 调，不是拒识卡的琥珀）
export const SYSTEM_ERROR_COPY = {
    barLine: 'The check could not be completed',
    // exp 三段拼接：中段在卡内加粗（This does <b>not</b> indicate…）
    expLead: 'The request did not go through, so coverage could not be verified. This does ',
    expBold: 'not',
    expTail: ' indicate that the patient is uninsured. Please try again in a few minutes.',
    // manual persona 专属脚注（managed 全程无计费文案）；no-charge 口径同原型——
    // 请求未到达 payer 不计费（与「每次 check 都收费」拍板不冲突：那条讲的是缓存命中）
    noCharge: 'The payer did not respond, so there is no charge.',
    // re-check 失败但已有历史结果：卡面不动，点击时刻的 toast 是全部反馈（原型 §6）
    toast: 'Could not reach the payer — nothing was charged',
};

// ---------- AAA 拒识修复卡 ----------
export const REJECTED_COPY = {
    // 数据类拒识（fixField 存在）统一的误读消除；payer 侧故障渲染各码自己的 plain
    exp: 'This does not indicate a lack of coverage. The payer could not match the details submitted, which is usually a data entry error.',
    recommendedFix: 'Recommended fix:',
    onFile: (fieldLabel: string) => `${fieldLabel} on file:`,
    alsoFlagged: (shorts: string) =>
        `Also flagged: ${shorts}. Addressed after the above, if it still fails on re-check.`,
    reference: 'Reference',
    unmappedNotice: 'This AAA code is not in our mapping table yet. Billing Ops has been notified.',
    requestCardBtn: 'Request updated insurance',
    messagePatientBtn: 'Message patient',
    await: (date: string) =>
        `Card requested ${date}. Waiting for the patient. Coverage will be re-checked automatically once the new card is on file, and you will be notified with the result.`,
};

// ---------- Request insurance card 弹窗 ----------
export const CARD_REQUEST_COPY = {
    title: 'Request updated insurance',
    noteLabel: 'Message to patient',
    noteHint: 'Sent with the document request. Edit it however you like.',
    channelsLabel: 'Preferred methods of send',
    channels: [
        { key: 'chat', label: 'In-app chat' },
        { key: 'text', label: 'Text message' },
        { key: 'email', label: 'Email' },
    ] as const,
    cancel: 'Cancel',
    send: 'Send request',
    sent: 'Request sent successfully.',
    sentNoChannel: 'Request saved. No message was sent.',
    // 仅 manual persona 且带 AAA 上下文：卡片到位自动 re-check 的计费说明
    autoRecheckNote:
        'Once the new card is on file, coverage is re-checked automatically. $0.25 if the payer answers, nothing if it fails again.',
};

// ---------- unmapped payer（FR4）+ advanced search ----------
export const UNMAPPED_COPY = {
    barLine: 'Confirm the payer first',
    expNoSearch: (carrier: string) =>
        `The carrier on file, "${carrier}", does not match a payer in our directory, so nothing has been sent. An advanced search looks for it across the full directory and returns the plans it could be.`,
    // enrolled 免费不带价格（2026-09-16 拍板）
    advCta: (price?: string) => (price ? `Run advanced search · ${price}` : 'Run advanced search'),
    expWithCandidates: (carrier: string, manualPriceNote: boolean) =>
        `The carrier on file, "${carrier}", does not match a payer in our directory. Sending to the wrong one returns a rejection${manualPriceNote ? ' that is still billable at $0.25.' : '.'}`,
    candidateSub: (payerId: string, note?: string) => `Payer ID ${payerId}${note ? ` · ${note}` : ''}`,
    footer: 'No request was submitted, so there is no charge.',
    // 选定候选并通知患者后的记录行（不重跑 check——record 上的 carrier 未被改写）
    routedNotice: (name: string, payerId: string, sentDate: string) =>
        `You picked ${name} · payer ID ${payerId}. Your patient was told ${sentDate}. The carrier on file is unchanged.`,
};

// cost modal 的 advanced 模式（$1.50，仅非 program 收费，无 Don't ask again）
export const ADV_COST_COPY = {
    title: 'Run an advanced payer search?',
    body: (carrier: string) => `Klarity will search the payer directory for plans matching ${carrier}.`,
    label: 'This search',
    // 主按钮是对动作的命名：这里买的是目录搜索，不是 check
    run: 'Run search',
};

// ---------- payer ID / NPI 设置流（原型 BILLING-NPI MODAL，逐字移植） ----------
export const NPI_MODAL_COPY = {
    npiTitle: 'What’s your billing NPI?',
    payerTitle: 'What’s your Payer ID?',
    npiIntro:
        'Whether you are in network, and what this patient owes you, both depend on which practice is asking. So ' +
        'every check is sent under an NPI, and it has to be the one your claims are billed under.',
    // owner 专属蓝色 scope 说明：这一步决定的是「替所有人开通」这件事本身
    ownerScope:
        'This switches eligibility checks on for every provider and staff member at your practice. Each check they ' +
        'run goes on the practice’s invoice.',
    onFileTitle: 'Use the NPI on my profile',
    // owner 下选档案 NPI 的后果说明（追加在号码行之后）
    onFileOwnerNote: '. Every provider at the practice would run checks under this one.',
    otherTitle: 'I have a different billing NPI',
    otherTitleOwner: 'Use my practice’s group NPI',
    otherDesc: 'Your claims go out under a practice or group NPI',
    otherDescOwner: 'The NPI your practice bills claims under. Everyone at the practice will use it.',
    recommended: 'Recommended',
    npiLabel: 'Billing NPI',
    npiTip: 'The NPI your claims are billed under. Look it up at nppes.cms.hhs.gov',
    npiPlaceholder: '10 digit NPI',
    // registry 查询中/命中/查不到的面板复用 NpiRegistryBox，文案随组件走，这里不重复维护
    // payer 步 intro；provider 级数据（用户拍板），与原型 practice-wide 措辞的差异见横幅注释
    payerIntro:
        'Every check needs a payer ID to know where to send the request. Fill in what you accept below, so future ' +
        'checks won’t need to ask again.',
    // owner 替 member 填时（chips 选中他人）
    payerIntroOwner: 'Filling in for a provider on your team saves to their own setup.',
    // plan 只读展示（2026-09-08 拍板）：数据来自 insurance survey，checkbox 禁用、无 Select all/Reset
    plansSelectHead: 'The plans you accept',
    hidePlans: 'Hide plans',
    showPlans: 'Show plans',
    idsHead: 'Payer IDs',
    idsHelp: 'Commercial and government plans use different Payer IDs. Find these on your existing claims or EOBs.',
    commercialLabel: 'Commercial plans',
    governmentLabel: 'Government plans',
    idPlaceholder: 'Enter Payer ID',
    next: 'Next',
    runCheck: 'Run check',
    save: 'Save',
    back: 'Back',
    cancel: 'Cancel',
    // 2026-09-15 拍板：Run check 门槛 = 所有行的 payer ID 填齐（无 this-check 单格闸）。
    // 2026-09-28：门槛从禁用按钮改成点击后的 toast，所以这句要自己说清楚缺在哪
    blockedToast: 'Fill in a payer ID for every carrier listed before running the check.',
    // owner 的多人 grid：点名缺 ID 的 provider，否则用户只会盯着当前这张 chip 找不到空格。
    // 超过两位就收成计数——toast 不是列清单的地方，chip 上的顺序才是
    blockedToastNamed: (names: string[]) => {
        const who = names.length <= 2 ? names.join(' and ') : `${names[0]}, ${names[1]} and ${names.length - 2} more`;
        return `Fill in a payer ID for every carrier under ${who} before running the check.`;
    },
};

// ---------- practice gap 横幅（蓝色 elig-owner-note；三种 gap，off 分支本期不建） ----------
export const GAP_COPY = {
    // billing NPI 未设置（practice 级）：member 无按钮——这不是他能做的事
    npiMember: 'Your practice owner needs to set up eligibility checks. They do it once, for everyone at the practice.',
    // member 点 check/Re-check 时的拦截 toast（billing NPI 只有 owner 能设）
    npiMemberToast: 'Your practice owner has to finish setting this up first',
    // payer ID 缺配（payerIdMapped=false，owner/member 都显示，2026-09-10 拍板）：
    // member 蓝色只留 Message owner（不自填，改动集中经 owner）；owner/solo 琥珀色 Set it up 自己补
    payerGap: (carrier: string, state: string) =>
        `No payer ID on file for ${carrier} in ${state} yet. Once it’s set up, this won’t come up again.`,
    messageOwner: 'Message owner',
    setItUp: 'Set it up',
};

// ---------- service template 缺 carrier 横幅（琥珀；优先于 payer ID 横幅——格子都不存在） ----------
export const TEMPLATE_GAP_COPY = {
    // my/solo：template 是自己的，直接去加（Add it 新开 service template 页）
    self: (carrier: string) =>
        `${carrier} isn’t on your service template, so this check can’t run. Add it there, then check again.`,
    // practice 视图：template 归被查的那位 provider，owner 改不了 → 不给按钮，也不点名
    // （与 TeamRequestBanner 同一口径：systemic, not personal）
    other: (carrier: string) =>
        `${carrier} isn’t on this provider’s service template, so this check can’t run. They need to add it there first.`,
    action: 'Add it',
    // Re-check 被拦时的 toast（结果卡已在、但 carrier 已不在 template 上）
    toast: 'Add this carrier to your service template first',
    toastOther: 'This provider needs to add the carrier to their service template first',
};

// ---------- team request 横幅（owner，琥珀 elig-update-note；不点名——systemic, not personal） ----------
export const TEAM_REQUEST_COPY = {
    one: 'A provider on your team needs a payer ID set up before their check can run.',
    many: (n: number) => `${n} providers on your team need a payer ID set up before their checks can run.`,
    action: 'Set it up',
};

// ---------- card update 提示条（琥珀；NOTICE 不带按钮——Re-check 就在下方 bar 上） ----------
export const CARD_UPDATE_COPY = (date: string) =>
    `Patient uploaded a new insurance card on ${date}. Your last check was against the previous one, so feel free ` +
    'to re-check below whenever it’s convenient.';

// ---------- Message owner（member 被 payer gap 卡住时的人工通道，草稿预填） ----------
export const MSG_OWNER_COPY = {
    title: 'Message practice owner',
    to: 'Practice owner',
    draft: (carrier: string, state: string) =>
        `Hi, I’m blocked running an eligibility check for ${carrier} (${state}) — we don’t have a payer ID set up ` +
        'for it yet. Could you add one when you get a chance?',
    send: 'Send message',
    sent: 'Message sent successfully',
};

// ---------- unmapped：选定候选后的患者确认弹窗（发送即结束；可关闭，关掉等于没选） ----------
export const ROUTE_MODAL_COPY = {
    title: 'Confirm the plan with the patient',
    body: (picked: string, onFile?: string) =>
        `You picked ${picked} for this check${onFile && onFile !== picked ? `, and the record still lists ${onFile}` : ''}. Let your patient know which plan you are billing, so a bill is not the first they hear of it.`,
    msgLabel: 'Message to patient',
    send: 'Send message',
    sent: 'Message sent successfully',
};

// ---------- History（标题行按钮 + 流水弹窗；整条记录，不属于任何一张结果卡） ----------
export const HISTORY_COPY = {
    button: 'History',
    title: 'Eligibility check history',
    // 只要有保险按钮就常驻（会随条数出没的按钮读起来像 bug）——没得可列时由弹窗自己说
    empty: 'No eligibility checks have run for this patient yet.',
    columns: {
        plan: 'Plan',
        // 10/21：Plan 与 Result 之间，这次 check 时那张卡的槽位
        insuranceType: 'Insurance type',
        result: 'Result',
        date: 'Checked date',
        by: 'Checked by',
    },
    // 结果卡上的那一次
    current: 'Active',
    insuranceType: { PRIMARY: 'Primary', SECONDARY: 'Secondary' },
    // 操作人与执行身份不同才印（owner 代查）
    ranAs: (provider: string) => `as ${provider}`,
};

// ---------- practice 视图未选 provider（原型 09/15：蓝色，不是琥珀——没出错，只是少一个选择） ----------
// 卡片请求同样以某位 provider 的身份发出（患者收到的是「你的 provider X 要你传新卡」），
// 没选人就没有发件人——按钮置灰，tooltip 指回上面的卡片
export const CHOOSE_PROVIDER_CARD_TIP =
    'Choose a provider above first. The request goes out under their name, to the record kept for them.';

export const CHOOSE_PROVIDER_COPY =
    'Choose a provider above to run this check as. It goes out under their payer ID, and the result is kept for them.';
